import { api } from '../api';

export type EmailProviderType = 'SMTP' | 'API';
export type EmailApiProvider = 'SENDIFY' | 'RESEND' | 'CUSTOM';

export interface EmailSettings {
  id?: string;
  userId?: string;
  providerType?: EmailProviderType;
  apiProvider?: EmailApiProvider | null;
  apiKey?: string;
  apiBaseUrl?: string | null;
  smtpHost?: string;
  smtpPort?: number;
  smtpSecure?: boolean;
  smtpUser?: string;
  smtpPass?: string;
  smtpFrom: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface PaymentSettings {
  id?: string;
  userId?: string;
  bankName?: string;
  bankAccount?: string;
  bankAccountName?: string;
  paymentMethods: string[];
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface SystemSettings {
  id: string;
  key: string;
  value: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DecimalSettings {
  amountDecimalPlaces: number;
  vatDecimalPlaces: number;
}

export interface PaymentGatewaySettings {
  id?: string;
  userId?: string;
  gatewayType: 'PAYOS' | 'MOMO' | 'VNPAY' | 'ZALOPAY' | 'VIETQR' | 'BANK_TRANSFER' | 'SHINHAN' | 'MB_PAYGATE' | 'MBB';
  isEnabled: boolean;
  config: any;
  createdAt?: string;
  updatedAt?: string;
}

export interface PayOSConfig {
  clientId: string;
  apiKey: string;
  checksumKey: string;
  environment: 'sandbox' | 'production';
}

export interface MoMoConfig {
  partnerCode: string;
  accessKey: string;
  secretKey: string;
  environment: 'sandbox' | 'production';
}

export interface VNPayConfig {
  tmnCode: string;
  secretKey: string;
  environment: 'sandbox' | 'production';
}

export interface ZaloPayConfig {
  appId: string;
  key1: string;
  key2: string;
  environment: 'sandbox' | 'production';
}

export interface VietQRConfig {
  // Credentials we provide to VietQR (integration identifier on our side)
  username: string;
  password: string;
  // Credentials provided by VietQR to us for Basic Auth when calling token_generate
  merchantUsername: string;
  merchantPassword: string;
  bankCode: string; // Bank code (VCB, TCB, MB, etc.)
  bankAccount: string; // Our bank account number
  userBankName: string; // Account holder name
  environment: 'sandbox' | 'production';
}

export interface BankTransferConfig {
  bankName: string;
  accountNumber: string;
  accountName: string;
  qrCode?: string;
}

export interface ShinhanConfig {
  clientId: string;
  clientSecret: string;
  motherAccountNumber: string;
  vaPrefix: string;
  merchantName: string;
  privateKey: string;
  shinhanPublicKey?: string;
  bankBin?: string;
  bankName?: string;
  environment: 'sandbox' | 'production' | 'dev';
  mockMode?: boolean;
  useVirtualAccount?: boolean;
  useBuildingVirtualAccount?: boolean;
}

export interface MbPaygateConfig {
  clientId: string;
  clientSecret: string;
  merchantId: string;
  accessCode: string;
  secretKey: string;
  baseUrl?: string;
  tokenUrl?: string;
  createOrderPath?: string;
  detailPath?: string;
  returnUrl?: string;
  ipnUrl?: string;
  defaultPayMethod?: string;
  orderRefChannel?: string;
  environment: 'sandbox' | 'production';
}

export interface MbbConfig {
  username: string;
  password: string;
  corpId: string;
  accountNumber: string;
  anticaptchaApiKey?: string;
  bankName?: string;
  bankBin?: string;
}

export interface FCMSettings {
  serviceAccount: {
    type?: string;
    project_id?: string;
    client_email?: string;
    private_key?: string;
    private_key_id?: string;
  };
  isEnabled: boolean;
  hasServiceAccount?: boolean;
}

export interface QrSettings {
  id?: string;
  paymentBankBin?: string;
  paymentBankCode?: string;
  paymentBankName?: string;
  paymentAccount?: string;
  paymentAccountName?: string;
  shinhanVirtualAccountNumber?: string;
  androidAppUrl?: string;
  iosAppUrl?: string;
  androidQrImagePath?: string;
  iosQrImagePath?: string;
  androidQrImageUrl?: string;
  iosQrImageUrl?: string;
}

export interface VietQRBankOption {
  bin: string;
  code: string;
  shortName: string;
  name: string;
}

export interface StorageSettings {
  storageType: 'local' | 's3';
  s3Config: {
    name: string;
    endpoint: string;
    region: string;
    accessKey: string;
    secretKey: string;
    bucketName: string;
  };
}

export interface LogoSettings {
  logoPath?: string | null;
  logoUrl?: string | null;
}

export const settingsApi = {
  // Email Settings
  getEmailSettings: async (global?: boolean): Promise<EmailSettings | null> => {
    const params = global ? { global: 'true' } : {};
    const response = await api.get('/settings/email', { params });
    return response.data;
  },

  getEffectiveEmailSettings: async (buildingId?: string): Promise<EmailSettings | null> => {
    const params: Record<string, string> = {};
    if (buildingId) params.buildingId = buildingId;
    const response = await api.get('/settings/email/effective', { params });
    return response.data;
  },

  getUserEmailSettings: async (buildingId?: string): Promise<EmailSettings | null> => {
    const params: Record<string, string> = {};
    if (buildingId) params.buildingId = buildingId;
    const response = await api.get('/settings/email/own', { params });
    return response.data;
  },

  saveEmailSettings: async (data: EmailSettings & { global?: boolean; buildingId?: string }): Promise<EmailSettings> => {
    const response = await api.post('/settings/email', data);
    return response.data;
  },

  testEmailConnection: async (
    global?: boolean,
    buildingId?: string,
  ): Promise<{ success: boolean; message: string }> => {
    const params: Record<string, string> = {};
    if (global) params.global = 'true';
    if (buildingId) params.buildingId = buildingId;
    const response = await api.post('/settings/email/test', {}, { params });
    return response.data;
  },

  deleteEmailSettings: async (global?: boolean, buildingId?: string): Promise<void> => {
    const params: Record<string, string> = {};
    if (global) params.global = 'true';
    if (buildingId) params.buildingId = buildingId;
    await api.delete('/settings/email', { params });
  },

  // Payment Settings
  getPaymentSettings: async (global?: boolean): Promise<PaymentSettings | null> => {
    const params = global ? { global: 'true' } : {};
    const response = await api.get('/settings/payment', { params });
    return response.data;
  },

  getEffectivePaymentSettings: async (): Promise<PaymentSettings | null> => {
    const response = await api.get('/settings/payment/effective');
    return response.data;
  },

  getUserPaymentSettings: async (): Promise<PaymentSettings | null> => {
    const response = await api.get('/settings/payment/own');
    return response.data;
  },

  savePaymentSettings: async (data: PaymentSettings & { global?: boolean }): Promise<PaymentSettings> => {
    const response = await api.post('/settings/payment', data);
    return response.data;
  },

  deletePaymentSettings: async (global?: boolean): Promise<void> => {
    const params = global ? { global: 'true' } : {};
    await api.delete('/settings/payment', { params });
  },

  // System Settings (SUPERADMIN only)
  getAllSystemSettings: async (): Promise<SystemSettings[]> => {
    const response = await api.get('/settings/system');
    return response.data;
  },

  getSystemSetting: async (key: string): Promise<SystemSettings | null> => {
    const response = await api.get(`/settings/system/${key}`);
    return response.data;
  },

  saveSystemSetting: async (data: {
    key: string;
    value: any;
    description?: string;
  }): Promise<SystemSettings> => {
    const response = await api.post('/settings/system', data);
    return response.data;
  },

  deleteSystemSetting: async (key: string): Promise<void> => {
    await api.delete(`/settings/system/${key}`);
  },

  // Decimal Settings
  getDecimalSettings: async (): Promise<DecimalSettings | null> => {
    const response = await api.get('/settings/decimal');
    return response.data;
  },

  getEffectiveDecimalSettings: async (): Promise<DecimalSettings> => {
    const response = await api.get('/settings/decimal/effective');
    return response.data;
  },

  saveDecimalSettings: async (data: DecimalSettings): Promise<void> => {
    const response = await api.post('/settings/decimal', data);
    return response.data;
  },

  deleteDecimalSettings: async (): Promise<void> => {
    await api.delete('/settings/decimal');
  },

  // Payment Gateway Settings
  getPaymentGatewaySettings: async (
    global?: boolean,
    buildingId?: string,
  ): Promise<PaymentGatewaySettings[]> => {
    const params: Record<string, string> = {};
    if (global) params.global = 'true';
    if (buildingId) params.buildingId = buildingId;
    const response = await api.get('/settings/payment-gateways', { params });
    return response.data;
  },

  savePaymentGatewaySettings: async (data: {
    gatewayType: string;
    config: any;
    isEnabled: boolean;
    global?: boolean;
    buildingId?: string;
  }): Promise<void> => {
    await api.post('/settings/payment-gateways', data);
  },

  deletePaymentGatewaySettings: async (
    gatewayType: string,
    global?: boolean,
    buildingId?: string,
  ): Promise<void> => {
    const params: Record<string, string> = {};
    if (global) params.global = 'true';
    if (buildingId) params.buildingId = buildingId;
    await api.delete(`/settings/payment-gateways/${gatewayType}`, { params });
  },

  testShinhanConnection: async (data?: {
    config?: ShinhanConfig;
    global?: boolean;
    buildingId?: string;
  }): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/payment-gateways/shinhan/test-connection', data ?? {});
    return response.data;
  },

  testMbPaygateConnection: async (data?: {
    config?: MbPaygateConfig;
    global?: boolean;
    buildingId?: string;
  }): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/payment-gateways/mb-paygate/test-connection', data ?? {});
    return response.data;
  },

  testMbbConnection: async (data?: {
    config?: MbbConfig;
    global?: boolean;
    buildingId?: string;
  }): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/payment-gateways/mbb/test-connection', data ?? {});
    return response.data;
  },

  // FCM Settings (SUPERADMIN only)
  getFCMSettings: async (): Promise<FCMSettings | null> => {
    const response = await api.get('/settings/fcm');
    return response.data;
  },

  saveFCMSettings: async (data: Pick<FCMSettings, 'isEnabled'> & { serviceAccount?: FCMSettings['serviceAccount'] }): Promise<void> => {
    await api.post('/settings/fcm', data);
  },

  testFCMConnection: async (): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/settings/fcm/test');
    return response.data;
  },

  deleteFCMSettings: async (): Promise<void> => {
    await api.delete('/settings/fcm');
  },

  // Storage Settings
  getStorageSettings: async (global?: boolean): Promise<StorageSettings | null> => {
    const params = global ? { global: 'true' } : {};
    const response = await api.get('/settings/storage', { params });
    return response.data;
  },

  getEffectiveStorageSettings: async (buildingId?: string): Promise<StorageSettings | null> => {
    const params: Record<string, string> = {};
    if (buildingId) params.buildingId = buildingId;
    const response = await api.get('/settings/storage/effective', { params });
    return response.data;
  },

  getUserStorageSettings: async (buildingId?: string): Promise<StorageSettings | null> => {
    const params: Record<string, string> = {};
    if (buildingId) params.buildingId = buildingId;
    const response = await api.get('/settings/storage/own', { params });
    return response.data;
  },

  saveStorageSettings: async (data: StorageSettings & { global?: boolean; buildingId?: string }): Promise<StorageSettings> => {
    const response = await api.post('/settings/storage', data);
    return response.data;
  },

  deleteStorageSettings: async (global?: boolean, buildingId?: string): Promise<void> => {
    const params: Record<string, string> = {};
    if (global) params.global = 'true';
    if (buildingId) params.buildingId = buildingId;
    await api.delete('/settings/storage', { params });
  },

  getQrBanks: async (): Promise<VietQRBankOption[]> => {
    const response = await api.get('/settings/qr/banks');
    return response.data;
  },

  getQrSettings: async (
    global?: boolean,
    buildingId?: string,
    options?: { direct?: boolean },
  ): Promise<QrSettings | null> => {
    const params: Record<string, string> = {};
    if (global) params.global = 'true';
    if (buildingId) params.buildingId = buildingId;
    if (options?.direct) params.direct = 'true';
    const response = await api.get('/settings/qr', { params });
    return response.data;
  },

  saveQrSettings: async (data: QrSettings & { global?: boolean; buildingId?: string }): Promise<QrSettings> => {
    const response = await api.post('/settings/qr', data);
    return response.data;
  },

  generatePaymentQrSample: async (data: {
    paymentBankBin: string;
    paymentAccount: string;
    paymentAccountName?: string;
    amount?: number;
    description?: string;
  }): Promise<Blob> => {
    const response = await api.post('/settings/qr/payment/sample', data, {
      responseType: 'blob',
    });
    return response.data;
  },

  generateAppQrImages: async (data: {
    androidAppUrl?: string;
    iosAppUrl?: string;
    global?: boolean;
    buildingId?: string;
  }): Promise<QrSettings> => {
    const response = await api.post('/settings/qr/app/generate', data);
    return response.data;
  },

  saveAppQrSettings: async (data: {
    androidAppUrl?: string;
    iosAppUrl?: string;
    global?: boolean;
    buildingId?: string;
  }): Promise<QrSettings | null> => {
    const response = await api.post('/settings/qr/app/save', data);
    return response.data;
  },

  clearPaymentQrSettings: async (data: {
    global?: boolean;
    buildingId?: string;
  }): Promise<QrSettings | null> => {
    const response = await api.post('/settings/qr/payment/clear', data);
    return response.data;
  },

  getPublicLogoSettings: async (): Promise<LogoSettings | null> => {
    const response = await api.get('/settings/logo/public');
    return response.data;
  },

  getEffectiveLogoSettings: async (): Promise<LogoSettings | null> => {
    const response = await api.get('/settings/logo/effective');
    return response.data;
  },

  getLogoSettings: async (global?: boolean): Promise<LogoSettings | null> => {
    const params = global ? { global: 'true' } : {};
    const response = await api.get('/settings/logo', { params });
    return response.data;
  },

  getUserLogoSettings: async (): Promise<LogoSettings | null> => {
    const response = await api.get('/settings/logo');
    return response.data;
  },

  saveLogoSettings: async (data: { logoPath: string; global?: boolean }): Promise<LogoSettings> => {
    const response = await api.post('/settings/logo', data);
    return response.data;
  },

  uploadLogo: async (file: File, global?: boolean): Promise<LogoSettings> => {
    const formData = new FormData();
    formData.append('file', file);
    const params = global ? { global: 'true' } : {};
    const response = await api.post('/settings/logo/upload', formData, {
      params,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteLogoSettings: async (global?: boolean): Promise<void> => {
    const params = global ? { global: 'true' } : {};
    await api.delete('/settings/logo', { params });
  },
}; 